import React, { useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  addPort,
  addTemplate,
  beginEditInstance,
  clearEquipment,
  duplicateInstance,
  duplicateTemplate,
  loadEquipment,
  makeTemplateFromInstance,
  removeInstance,
  removePort,
  removeTemplate,
  renameTemplate,
  selectInstance,
  selectTemplate,
  setEquipmentReviewSelection,
  setSelectedEquipmentReviewStatus,
  resetDxfEquipmentReview,
  toggleEquipmentReviewSelection,
  setPortEditInstance,
  toggleEquipmentVisible,
  toggleProcessVisible,
  setAllProcessesVisible,
  toggleShowPorts,
  updateInstance,
} from '../../features/equipment/equipmentSlice';
import { activateCanvasTool } from '../../features/ui/activateCanvasTool';
import { selectExportableEquipmentInstances } from '../../features/equipment/equipmentSelectors';
import { makePort, nextPortIndex, canRemovePort, labelPortKind, resolvePortKind, portColor } from '../../utils/ports';
import { VisibilityIcon } from '../icons/ContextMenuIcons';
import {
  CATEGORY_LABELS,
  OBJECT_TYPE_LABELS,
} from '../../features/equipment/equipmentConstants';
import {
  resolvePorts,
  equipmentProcessKey,
  equipmentProcess,
  PROCESS_NONE_KEY,
  PROCESS_NONE_LABEL,
  equipmentProvenance,
  EQUIPMENT_PROVENANCE_LABEL,
  equipmentReviewStatus,
  polygonBounds,
  type EquipmentProvenance,
} from '../../utils/equipmentGeometry';
import { exportToJSON, importFromJSON } from '../../utils/storage';
import {
  buildLayoutSaveProject,
  createLayoutExportBase,
  equipmentDocToLayer,
  equipmentLoadPayload,
  extractEquipmentLayer,
  fabExportFilename,
} from '../../utils/projectExport';
import EquipmentTemplateModal from './EquipmentTemplateModal';

const EquipmentLibraryPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const draw = useAppSelector((s) => s.equipment.draw);
  const visible = useAppSelector((s) => s.equipment.visible);
  const showLabels = useAppSelector((s) => s.equipment.showLabels);
  const showPorts = useAppSelector((s) => s.equipment.showPorts);
  const hiddenProcesses = useAppSelector((s) => s.equipment.hiddenProcesses);
  const templates = useAppSelector((s) => s.equipment.templates);
  const templateOrder = useAppSelector((s) => s.equipment.templateOrder);
  const instances = useAppSelector((s) => s.equipment.instances);
  const instanceOrder = useAppSelector((s) => s.equipment.instanceOrder);
  const exportableInstances = useAppSelector(selectExportableEquipmentInstances);
  const selectedId = useAppSelector((s) => s.equipment.selectedInstanceId);
  const portEditId = useAppSelector((s) => s.equipment.portEditInstanceId);
  const layoutSaveMarkedIds = useAppSelector((s) => s.equipment.layoutSaveMarkedIds);
  const reviewSelectedIds = useAppSelector((s) => s.equipment.reviewSelectedIds);
  const floorPlanName = useAppSelector((s) => s.floorplan.name);
  const floorPlanId = useAppSelector((s) => s.floorplan.floorPlanId);
  const layoutElements = useAppSelector((s) => s.layout.elements);
  const layoutElementOrder = useAppSelector((s) => s.layout.elementOrder);
  const trackNodeAnnotations = useAppSelector((s) => s.layout.trackNodeAnnotations);
  const layoutLayers = useAppSelector((s) => s.layout.layers);
  const layoutLayerOrder = useAppSelector((s) => s.layout.layerOrder);
  const zoomLevel = useAppSelector((s) => s.ui.zoomLevel);
  const gridSettings = useAppSelector((s) => s.ui.gridSettings);
  const layersState = useAppSelector((s) => s.layers);

  const selected = selectedId ? instances[selectedId] : null;
  const [templateModal, setTemplateModal] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null,
  });
  const [sourceFilter, setSourceFilter] = useState<'all' | EquipmentProvenance>('all');
  const fileRef = useRef<HTMLInputElement>(null);

  const orderedTemplates = useMemo(
    () => templateOrder.map((id) => templates[id]).filter(Boolean),
    [templateOrder, templates]
  );

  /** Everything except rejected equipment, which the user has excluded. */
  const activeInstanceOrder = useMemo(
    () =>
      instanceOrder.filter((id) => {
        const inst = instances[id];
        return Boolean(inst) && equipmentReviewStatus(inst) !== 'rejected';
      }),
    [instanceOrder, instances]
  );

  /** Distinct processes (from work_cell) across all instances, with counts. */
  const processGroups = useMemo(() => {
    const counts = new Map<string, { key: string; label: string; count: number }>();
    for (const id of activeInstanceOrder) {
      const inst = instances[id];
      if (!inst) continue;
      const key = equipmentProcessKey(inst);
      const label = key === PROCESS_NONE_KEY ? PROCESS_NONE_LABEL : equipmentProcess(inst)!;
      const entry = counts.get(key);
      if (entry) entry.count += 1;
      else counts.set(key, { key, label, count: 1 });
    }
    return Array.from(counts.values()).sort((a, b) => {
      if (a.key === PROCESS_NONE_KEY) return 1;
      if (b.key === PROCESS_NONE_KEY) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [activeInstanceOrder, instances]);

  const allProcessKeys = useMemo(() => processGroups.map((g) => g.key), [processGroups]);

  const dxfReview = useMemo(() => {
    const generated = instanceOrder
      .map((id) => instances[id])
      .filter((inst) => inst && equipmentProvenance(inst) === 'dxf');
    return {
      potential: generated.filter((inst) => equipmentReviewStatus(inst) === 'potential'),
      accepted: generated.filter((inst) => equipmentReviewStatus(inst) === 'accepted'),
      rejected: generated.filter((inst) => equipmentReviewStatus(inst) === 'rejected'),
    };
  }, [instanceOrder, instances]);

  const handleExport = () => {
    const layoutBase = createLayoutExportBase({
      elements: layoutElements,
      elementOrder: layoutElementOrder,
      layers: layoutLayers,
      layerOrder: layoutLayerOrder,
      groups: layersState.groups,
      viewport: { x: 0, y: 0, scale: zoomLevel },
      gridSettings,
      trackNodeAnnotations,
      name: 'Fab Layout',
    });

    const project = buildLayoutSaveProject({
      layoutBase,
      templates: Object.values(templates),
      instances: exportableInstances,
      markedIds: layoutSaveMarkedIds,
      floorPlanId,
      floorPlanName,
      equipmentUi: {
        visible,
        showLabels,
        showPorts,
      },
    });

    exportToJSON(project, fabExportFilename(floorPlanName));
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const parsed = await importFromJSON(file);
      if (parsed.type === 'equipment') {
        dispatch(loadEquipment(equipmentLoadPayload(equipmentDocToLayer(parsed.doc))));
        return;
      }

      const layer = extractEquipmentLayer(parsed.project);
      if (layer) {
        dispatch(loadEquipment(equipmentLoadPayload(layer)));
        return;
      }

      alert('No equipment layer found in this file. Use toolbar Import JSON for layout-only projects.');
    } catch (err) {
      console.error('Failed to import equipment layer:', err);
      alert('Could not import this file. Ensure it is a fab-project or equipment-layer JSON.');
    }
  };

  const selectedPorts = selected ? resolvePorts(selected, selected.templateId ? templates[selected.templateId] : null) : [];

  const btn =
    'px-2 py-1 rounded text-xs font-medium border transition-colors';

  return (
    <div className="p-3 space-y-4 text-sm">
      {/* Tool controls */}
      <div className="space-y-2">
        <button
          type="button"
          onClick={() =>
            dispatch(activateCanvasTool(draw.active ? null : 'equipment-draw'))
          }
          className={`w-full py-2 rounded-md text-sm font-semibold text-white ${
            draw.active ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {draw.active ? 'Stop Identifying' : '＋ Identify Equipment'}
        </button>

        {draw.active && (
          <p className="text-xs text-gray-500">
            Click two opposite corners to identify a rectangular footprint.
          </p>
        )}

        <div className="flex flex-wrap gap-1.5">
          <ToggleChip active={visible} onClick={() => dispatch(toggleEquipmentVisible())}>
            Overlay
          </ToggleChip>
          <ToggleChip active={showPorts} onClick={() => dispatch(toggleShowPorts())}>
            Ports
          </ToggleChip>
        </div>

        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={handleExport}
            className={`${btn} flex-1 border-gray-300 text-gray-700 hover:bg-gray-50`}
          >
            Export fab JSON
          </button>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className={`${btn} flex-1 border-gray-300 text-gray-700 hover:bg-gray-50`}
          >
            Import fab JSON
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".json,application/json"
            onChange={handleImport}
            className="hidden"
          />
        </div>
      </div>

      {/* DXF potential-equipment review */}
      {(dxfReview.potential.length > 0 ||
        dxfReview.accepted.length > 0 ||
        dxfReview.rejected.length > 0) && (
        <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-3">
          <div>
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Potential equipment
            </div>
            <div className="mt-1 text-[11px] text-gray-500">
              {dxfReview.potential.length} potential · {dxfReview.accepted.length} accepted ·{' '}
              {dxfReview.rejected.length} rejected
            </div>
          </div>

          {dxfReview.potential.length > 0 && (
            <>
              <div className="flex items-center justify-between text-[11px]">
                <span className="font-medium text-gray-600">
                  {reviewSelectedIds.length} selected
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      dispatch(
                        setEquipmentReviewSelection(dxfReview.potential.map((inst) => inst.id))
                      )
                    }
                    className="font-medium text-blue-600 hover:text-blue-800"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => dispatch(setEquipmentReviewSelection([]))}
                    className="font-medium text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
                {dxfReview.potential.map((inst) => {
                  const bounds = polygonBounds(inst.polygon);
                  const width = Math.max(0, bounds.maxX - bounds.minX);
                  const height = Math.max(0, bounds.maxY - bounds.minY);
                  const layer =
                    typeof inst.metadata?.layer === 'string' ? inst.metadata.layer : 'Unknown layer';
                  const checked = reviewSelectedIds.includes(inst.id);
                  return (
                    <button
                      key={inst.id}
                      type="button"
                      onClick={() => dispatch(toggleEquipmentReviewSelection(inst.id))}
                      className={`flex w-full items-start gap-2 rounded-md border px-2 py-2 text-left transition-colors ${
                        checked
                          ? 'border-amber-400 bg-amber-50'
                          : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                          checked
                            ? 'border-amber-500 bg-amber-500 text-white'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {checked ? '✓' : ''}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-xs font-semibold text-gray-800">
                          {inst.name}
                        </span>
                        <span className="block truncate text-[10px] text-gray-500">{layer}</span>
                        <span className="block text-[10px] text-gray-400">
                          {width.toFixed(1)} × {height.toFixed(1)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={reviewSelectedIds.length === 0}
                  onClick={() => dispatch(setSelectedEquipmentReviewStatus('accepted'))}
                  className="rounded-md bg-blue-600 px-2 py-2 text-xs font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Accept {reviewSelectedIds.length || ''}
                </button>
                <button
                  type="button"
                  disabled={reviewSelectedIds.length === 0}
                  onClick={() => dispatch(setSelectedEquipmentReviewStatus('rejected'))}
                  className="rounded-md border border-red-300 px-2 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Reject selected
                </button>
              </div>
            </>
          )}

          {(dxfReview.accepted.length > 0 || dxfReview.rejected.length > 0) && (
            <button
              type="button"
              onClick={() => dispatch(resetDxfEquipmentReview())}
              className="text-[11px] font-medium text-gray-500 hover:text-gray-700"
            >
              Reset review
            </button>
          )}
        </div>
      )}

      {/* Process visibility */}
      {processGroups.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Process visibility
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  dispatch(setAllProcessesVisible({ processes: allProcessKeys, visible: true }))
                }
                className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
              >
                Show all
              </button>
              <button
                type="button"
                onClick={() =>
                  dispatch(setAllProcessesVisible({ processes: allProcessKeys, visible: false }))
                }
                className="text-[11px] font-medium text-gray-500 hover:text-gray-700"
              >
                Hide all
              </button>
            </div>
          </div>

          {!visible && (
            <div className="text-[10px] text-amber-600">
              Turn on the “Overlay” toggle above to see equipment on the canvas.
            </div>
          )}

          <div className="space-y-1">
            {processGroups.map((g) => {
              const isVisible = !hiddenProcesses[g.key];
              return (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => dispatch(toggleProcessVisible(g.key))}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${
                    isVisible ? 'hover:bg-gray-100' : 'opacity-50 hover:bg-gray-100'
                  }`}
                  title={isVisible ? `Hide ${g.label}` : `Show ${g.label}`}
                >
                  <VisibilityIcon
                    visible={isVisible}
                    className="w-3.5 h-3.5 shrink-0 text-gray-600"
                  />
                  <span
                    className={`flex-1 truncate font-medium ${
                      g.key === PROCESS_NONE_KEY ? 'text-gray-400 italic' : 'text-gray-800'
                    }`}
                  >
                    {g.label}
                  </span>
                  <span className="text-[10px] text-gray-400 tabular-nums shrink-0">{g.count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected instance properties */}
      {selected && (
        <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Selected Object
            </div>
            <button
              type="button"
              onClick={() => dispatch(selectInstance(null))}
              className="text-xs text-gray-400 hover:text-gray-600"
            >
              Deselect
            </button>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 mb-1">Name</label>
            <input
              value={selected.name}
              onChange={(e) =>
                dispatch(
                  updateInstance({
                    id: selected.id,
                    changes: { name: e.target.value },
                    skipHistory: true,
                  })
                )
              }
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
            <div>
              <span className="text-gray-400">Type:</span> {OBJECT_TYPE_LABELS[selected.objectType]}
            </div>
            <div>
              <span className="text-gray-400">Category:</span> {CATEGORY_LABELS[selected.category]}
            </div>
            <div>
              <span className="text-gray-400">Template:</span>{' '}
              {selected.templateId ? templates[selected.templateId]?.name ?? '—' : 'None'}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-400">Rotation:</span>
              <input
                type="number"
                value={selected.rotation}
                onChange={(e) =>
                  dispatch(
                    updateInstance({
                      id: selected.id,
                      changes: { rotation: Number(e.target.value) || 0 },
                      skipHistory: true,
                    })
                  )
                }
                className="w-14 rounded border border-gray-300 px-1 py-0.5 text-xs"
              />
              °
            </div>
          </div>

          {selected.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selected.tags.map((t) => (
                <span
                  key={t}
                  className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Ports */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[11px] font-semibold text-gray-500">
                Ports ({selectedPorts.length})
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    dispatch(
                      addPort({
                        instanceId: selected.id,
                        port: makePort({
                          parentId: selected.id,
                          parentType:
                            selected.objectType === 'stocker' ? 'stocker' : 'equipment',
                          name: `Port ${nextPortIndex(selectedPorts)}`,
                          rx: 1,
                          ry: 0.5,
                          edge: 'right',
                          type: 'bidirectional',
                        }),
                      })
                    )
                  }
                  className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                >
                  + Add
                </button>
                <button
                  type="button"
                  onClick={() =>
                    dispatch(setPortEditInstance(portEditId === selected.id ? null : selected.id))
                  }
                  className={`text-[11px] font-medium ${
                    portEditId === selected.id ? 'text-red-600' : 'text-blue-600'
                  } hover:underline`}
                >
                  {portEditId === selected.id ? 'Done' : 'Drag on canvas'}
                </button>
              </div>
            </div>
            {selectedPorts.length > 0 && (
              <div className="space-y-1">
                {selectedPorts.map((p) => {
                  const parentType =
                    selected.objectType === 'stocker' ? 'stocker' : 'equipment';
                  return (
                    <div
                      key={p.id}
                      className="flex items-center justify-between text-[11px] text-gray-600 bg-gray-50 rounded px-2 py-1"
                    >
                      <span className="truncate">
                        <span
                          className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                          style={{ backgroundColor: portColor(p, parentType) }}
                        />
                        {p.name} · {labelPortKind(resolvePortKind(p, parentType))}
                      </span>
                      <button
                        type="button"
                        disabled={!canRemovePort(selectedPorts)}
                        title={
                          canRemovePort(selectedPorts)
                            ? 'Remove port'
                            : 'Each object must keep at least one port'
                        }
                        onClick={() =>
                          dispatch(removePort({ instanceId: selected.id, portId: p.id }))
                        }
                        className="text-red-400 hover:text-red-600 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:text-gray-300"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => dispatch(beginEditInstance(selected.id))}
              className={`${btn} border-blue-300 text-blue-700 hover:bg-blue-50`}
            >
              Edit…
            </button>
            <button
              type="button"
              onClick={() => dispatch(duplicateInstance(selected.id))}
              className={`${btn} border-gray-300 text-gray-700 hover:bg-gray-50`}
            >
              Duplicate
            </button>
            <button
              type="button"
              onClick={() => {
                const name = window.prompt('Template name', selected.name);
                if (name) {
                  dispatch(addTemplate(makeTemplateFromInstance(selected, templates, name)));
                }
              }}
              className={`${btn} border-gray-300 text-gray-700 hover:bg-gray-50`}
            >
              Save as Template
            </button>
            <button
              type="button"
              onClick={() => dispatch(removeInstance(selected.id))}
              className={`${btn} border-red-300 text-red-600 hover:bg-red-50`}
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* Equipment Library (templates) */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Equipment Library
          </div>
          <button
            type="button"
            onClick={() => setTemplateModal({ open: true, id: null })}
            className="text-xs font-medium text-blue-600 hover:text-blue-800"
          >
            + New Template
          </button>
        </div>

        {orderedTemplates.length === 0 ? (
          <div className="text-xs text-gray-400 italic py-2">
            No templates yet. Identify an object and choose “Save as Template”, or create one here.
          </div>
        ) : (
          <div className="space-y-1.5">
            {orderedTemplates.map((t) => (
              <div
                key={t.id}
                className="group flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-2 hover:border-blue-300"
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-base"
                  style={{ backgroundColor: `${t.defaultStyle.color}22` }}
                >
                  {t.icon}
                </div>
                <button
                  type="button"
                  onClick={() => dispatch(selectTemplate(t.id))}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="truncate text-sm font-medium text-gray-800">{t.name}</div>
                  <div className="truncate text-[11px] text-gray-400">
                    {OBJECT_TYPE_LABELS[t.objectType]} · {CATEGORY_LABELS[t.category]} ·{' '}
                    {t.ports.length} ports
                  </div>
                </button>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconBtn
                    title="Edit"
                    onClick={() => setTemplateModal({ open: true, id: t.id })}
                  >
                    ✎
                  </IconBtn>
                  <IconBtn
                    title="Rename"
                    onClick={() => {
                      const name = window.prompt('Rename template', t.name);
                      if (name) dispatch(renameTemplate({ id: t.id, name }));
                    }}
                  >
                    Aa
                  </IconBtn>
                  <IconBtn title="Duplicate" onClick={() => dispatch(duplicateTemplate(t.id))}>
                    ⧉
                  </IconBtn>
                  <IconBtn
                    title="Delete"
                    danger
                    onClick={() => {
                      if (confirm(`Delete template “${t.name}”?`)) {
                        dispatch(removeTemplate(t.id));
                      }
                    }}
                  >
                    🗑
                  </IconBtn>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instances list */}
      {activeInstanceOrder.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Identified Objects ({activeInstanceOrder.length})
          </div>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(
              [
                ['all', 'All'],
                ['dxf', 'DXF'],
                ['layout', 'Layout'],
                ['manual', 'Manual'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSourceFilter(key)}
                className={`px-2 py-0.5 rounded text-[10px] font-medium border ${
                  sourceFilter === key
                    ? key === 'layout'
                      ? 'bg-red-50 border-red-400 text-red-800'
                      : key === 'dxf'
                        ? 'bg-slate-100 border-slate-400 text-slate-700'
                        : 'bg-blue-50 border-blue-400 text-blue-700'
                    : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gray-400 mb-2 leading-snug">
            Layout equipment shows after Load Layout. Identify Equipment (or Overlay) reveals
            DXF extract footprints. Ports only on saved items.
          </p>
          <div className="space-y-1">
            {activeInstanceOrder.map((id) => {
              const inst = instances[id];
              if (!inst) return null;
              const provenance = equipmentProvenance(inst);
              if (sourceFilter !== 'all' && provenance !== sourceFilter) return null;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => dispatch(selectInstance(id))}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs ${
                    id === selectedId ? 'bg-blue-50 text-blue-800' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span className="truncate flex-1">{inst.name}</span>
                  <ProvenanceChip provenance={provenance} />
                  <span className="text-[10px] text-gray-400">
                    {OBJECT_TYPE_LABELS[inst.objectType]}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              if (confirm('Clear ALL equipment objects and templates?')) {
                dispatch(clearEquipment());
              }
            }}
            className="mt-2 text-[11px] text-red-500 hover:text-red-700"
          >
            Clear equipment layer
          </button>
        </div>
      )}

      <EquipmentTemplateModal
        open={templateModal.open}
        templateId={templateModal.id}
        onClose={() => setTemplateModal({ open: false, id: null })}
      />
    </div>
  );
};

const ToggleChip: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-2 py-1 rounded text-xs font-medium border ${
      active
        ? 'bg-blue-50 border-blue-400 text-blue-700'
        : 'border-gray-300 text-gray-400 line-through'
    }`}
  >
    {children}
  </button>
);

const ProvenanceChip: React.FC<{ provenance: EquipmentProvenance }> = ({ provenance }) => {
  const label =
    provenance === 'layout' ? 'Layout' : provenance === 'dxf' ? 'DXF' : 'Manual';
  const className =
    provenance === 'layout'
      ? 'bg-red-50 text-red-800 border-red-300'
      : provenance === 'dxf'
        ? 'bg-slate-100 text-slate-700 border-slate-300'
        : 'bg-gray-50 text-gray-600 border-gray-300';
  return (
    <span
      className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${className}`}
      title={EQUIPMENT_PROVENANCE_LABEL[provenance]}
    >
      {label}
    </span>
  );
};

const IconBtn: React.FC<{
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ title, onClick, danger, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`h-6 w-6 rounded text-xs flex items-center justify-center ${
      danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-500 hover:bg-gray-100'
    }`}
  >
    {children}
  </button>
);

export default EquipmentLibraryPanel;
